import type { Asset } from '@cherry/shared';
import { shortenAddress } from '@cherry/shared';
import { useJettonMetadata } from '../hooks/useJettonMetadata';
import { useNftMetadata } from '../hooks/useNftMetadata';

const kindConfig: Record<string, { bg: string; icon: string; label: string }> = {
  TON:         { bg: 'bg-ton-500',      icon: '💎', label: 'TON' },
  JETTON:      { bg: 'bg-green-600',    icon: '🪙', label: 'Jetton' },
  NFT:         { bg: 'bg-purple-600',   icon: '🖼️', label: 'NFT' },
  TG_GIFT:     { bg: 'bg-cherry-500',   icon: '🎁', label: 'Gift' },
  TG_NUMBER:   { bg: 'bg-orange-500',   icon: '📞', label: 'TG Number' },
  TG_USERNAME: { bg: 'bg-pink-500',     icon: '@',  label: 'Username' },
  DNS:         { bg: 'bg-violet-600',   icon: '🌐', label: '.ton' },
};

function formatTon(nano: string) {
  return (Number(nano) / 1_000_000_000).toFixed(2);
}

function JettonBadge({ asset }: { asset: Extract<Asset, { kind: 'JETTON' }> }) {
  const { data: meta } = useJettonMetadata(asset.master);
  const cfg = kindConfig.JETTON;

  const decimals = meta?.decimals ?? 9;
  const rawAmount = Number(asset.amount) / 10 ** decimals;
  const displayAmount = rawAmount >= 1_000_000
    ? `${(rawAmount / 1_000_000).toFixed(1)}M`
    : rawAmount >= 1_000
      ? `${(rawAmount / 1_000).toFixed(1)}K`
      : rawAmount % 1 === 0
        ? rawAmount.toFixed(0)
        : rawAmount.toFixed(2);

  const symbol = meta?.symbol ?? shortenAddress(asset.master);
  const icon = meta?.imageUrl;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold text-white ${cfg.bg}`}
      title={meta?.name ? `${meta.name} (${asset.master})` : asset.master}
    >
      {icon ? (
        <img src={icon} alt="" className="w-5 h-5 rounded-full object-cover" loading="lazy" />
      ) : (
        <span aria-hidden>{cfg.icon}</span>
      )}
      {displayAmount} {symbol}
    </span>
  );
}

type NftLikeAsset = Extract<Asset, { kind: 'NFT' | 'TG_GIFT' | 'TG_NUMBER' | 'TG_USERNAME' | 'DNS' }>;

function NftBadge({ asset }: { asset: NftLikeAsset }) {
  const { data: meta, isLoading } = useNftMetadata(asset.address);
  const cfg = kindConfig[asset.kind];

  const displayName = meta?.name || shortenAddress(asset.address);
  const collectionLabel = meta?.collectionName;
  const isUnverified = !isLoading && !collectionLabel;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-semibold text-white ${cfg.bg}`}
      title={`${meta?.name || asset.kind} — ${asset.address}${collectionLabel ? ` (${collectionLabel})` : ''}`}
    >
      {meta?.image ? (
        <img
          src={meta.image}
          alt=""
          className="w-5 h-5 rounded object-cover"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <span aria-hidden>{cfg.icon}</span>
      )}
      <span className="truncate max-w-[120px]">{displayName}</span>
      {collectionLabel && (
        <span className="opacity-75 text-[0.625rem] font-normal truncate max-w-[80px]">{collectionLabel}</span>
      )}
      {isUnverified && asset.kind === 'NFT' && (
        <span className="opacity-60 text-[0.5625rem] font-normal italic">unverified</span>
      )}
    </span>
  );
}

export function AssetBadge({ asset }: { asset: Asset }) {
  if (asset.kind === 'TON' && (!asset.amountNano || asset.amountNano === '0')) {
    return null;
  }

  if (asset.kind === 'JETTON') {
    return <JettonBadge asset={asset} />;
  }

  if (asset.kind === 'NFT' || asset.kind === 'TG_GIFT' || asset.kind === 'TG_NUMBER' || asset.kind === 'TG_USERNAME' || asset.kind === 'DNS') {
    return <NftBadge asset={asset} />;
  }

  const cfg = kindConfig[asset.kind];
  if (!cfg) return null;

  const detail = asset.kind === 'TON' ? `${formatTon(asset.amountNano)} TON` : '';

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold text-white ${cfg.bg}`}
    >
      <span aria-hidden>{cfg.icon}</span>
      {detail}
    </span>
  );
}

const ASSET_SORT_ORDER: Record<string, number> = { NFT: 0, TG_GIFT: 0, TG_NUMBER: 0, TG_USERNAME: 0, DNS: 0, JETTON: 1, TON: 2 };

export function sortAssetsForDisplay(assets: Asset[]): Asset[] {
  return [...assets].sort((a, b) => (ASSET_SORT_ORDER[a.kind] ?? 1) - (ASSET_SORT_ORDER[b.kind] ?? 1));
}
